import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useRealtimeCases } from '@/hooks/useRealtimeCases';
import { Link } from 'react-router-dom';
import {
  Activity,
  Clock,
  User,
  MapPin,
  CheckCircle,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { IconGurney, IconOrLamp, IconVitalsMonitor, MarkOperative, MarkPostOp } from '@/components/medical/MedicalIcons';

interface OrCase {
  case_id: number;
  case_number: string;
  patient_name: string;
  procedure_name: string;
  surgeon_name: string;
  or_room: string;
  case_status: string;
  actual_start: string;
  incision_time: string | null;
  estimated_duration_minutes: number;
}

export function LiveCaseConsoleRoute() {
  useRealtimeCases();

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ['active-cases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_cases')
        .select('*')
        .in('case_status', [
          'patient_in_room',
          'anesthesia_start',
          'prep_start',
          'incision',
          'in_progress',
          'closing',
        ])
        .order('actual_start', { ascending: false });

      if (error) throw error;
      return data as OrCase[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      patient_in_room: 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30',
      anesthesia_start: 'bg-pt-purple/20 text-pt-purple border-pt-purple/30',
      prep_start: 'bg-pt-amber/20 text-pt-amber border-pt-amber/30',
      incision: 'bg-pt-orange/20 text-pt-orange border-pt-orange/30',
      in_progress: 'bg-pt-green/20 text-pt-green border-pt-green/30',
      closing: 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30',
    };
    return colors[status] || 'bg-pt-text-dim/20 text-pt-text border-pt-input-border';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'patient_in_room':
      case 'prep_start':       return <IconGurney size={15} />;
      case 'anesthesia_start': return <IconVitalsMonitor size={15} />;
      case 'incision':
      case 'in_progress':      return <MarkOperative size={15} />;
      case 'closing':
      case 'wound_closed':
      case 'recovery':         return <MarkPostOp size={15} />;
      case 'completed':        return <CheckCircle className="w-4 h-4" />;
      default:                 return <IconOrLamp size={15} />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      patient_in_room: 'Patient in Room',
      anesthesia_start: 'Anesthesia Start',
      prep_start: 'Prep in Progress',
      incision: 'Incision Made',
      in_progress: 'Surgery in Progress',
      closing: 'Closing',
      wound_closed: 'Wound Closed',
      recovery: 'In Recovery',
      completed: 'Completed',
    };
    return labels[status] || status;
  };

  const calculateElapsedTime = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - start.getTime()) / 1000 / 60);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-pt-text">Live Case Console</h1>
          <p className="text-pt-text-muted mt-1">Real-time operating room monitoring</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-pt-dark rounded-lg border border-pt-border">
            <Activity className="w-5 h-5 text-pt-green animate-pulse" />
            <span className="text-sm text-pt-text">
              {cases.length} Active {cases.length === 1 ? 'Case' : 'Cases'}
            </span>
          </div>
        </div>
      </div>

      {/* Active Cases Grid */}
      {cases.length === 0 ? (
        <EmptyState title="No active cases" hint="All operating rooms are currently available." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {cases.map((caseItem) => (
            <Link
              key={caseItem.case_id}
              to={`/live-cases/${caseItem.case_id}`}
              className="block bg-pt-dark rounded-lg border border-pt-border hover:border-pt-gold/40 transition-all p-6 group"
            >
              {/* Case Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-pt-gold" />
                    <span className="font-semibold text-pt-text">{caseItem.or_room}</span>
                    <span className="text-sm text-pt-text-dim">•</span>
                    <span className="text-sm text-pt-text-muted">{caseItem.case_number}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-pt-text group-hover:text-pt-gold transition-colors">
                    {caseItem.procedure_name}
                  </h3>
                </div>
                <div
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 ${getStatusColor(
                    caseItem.case_status
                  )}`}
                >
                  {getStatusIcon(caseItem.case_status)}
                  {getStatusLabel(caseItem.case_status)}
                </div>
              </div>

              {/* Patient & Surgeon */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-pt-text-dim" />
                  <div>
                    <p className="text-xs text-pt-text-dim">Patient</p>
                    <p className="text-sm text-pt-text">{caseItem.patient_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-pt-text-dim" />
                  <div>
                    <p className="text-xs text-pt-text-dim">Surgeon</p>
                    <p className="text-sm text-pt-text">{caseItem.surgeon_name}</p>
                  </div>
                </div>
              </div>

              {/* Timing */}
              <div className="flex items-center gap-4 pt-4 border-t border-pt-border">
                <div className="flex items-center gap-2 text-pt-text-muted">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    Elapsed: {calculateElapsedTime(caseItem.actual_start)}
                  </span>
                </div>
                {caseItem.estimated_duration_minutes && (
                  <div className="flex items-center gap-2 text-pt-text-muted">
                    <span className="text-sm">
                      Est: {Math.floor(caseItem.estimated_duration_minutes / 60)}h{' '}
                      {caseItem.estimated_duration_minutes % 60}m
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-pt-dark rounded-lg border border-pt-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pt-text-muted">In Progress</p>
              <p className="text-2xl font-bold text-pt-text mt-1">
                {cases.filter((c) => c.case_status === 'in_progress').length}
              </p>
            </div>
            <Activity className="w-8 h-8 text-pt-green" />
          </div>
        </div>

        <div className="bg-pt-dark rounded-lg border border-pt-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pt-text-muted">Closing</p>
              <p className="text-2xl font-bold text-pt-text mt-1">
                {cases.filter((c) => c.case_status === 'closing').length}
              </p>
            </div>
            <MarkPostOp size={30} className="text-pt-cyan" />
          </div>
        </div>

        <div className="bg-pt-dark rounded-lg border border-pt-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pt-text-muted">Setup/Prep</p>
              <p className="text-2xl font-bold text-pt-text mt-1">
                {
                  cases.filter((c) =>
                    ['patient_in_room', 'anesthesia_start', 'prep_start'].includes(c.case_status)
                  ).length
                }
              </p>
            </div>
            <IconGurney size={30} className="text-pt-amber" />
          </div>
        </div>

        <div className="bg-pt-dark rounded-lg border border-pt-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pt-text-muted">Total Active</p>
              <p className="text-2xl font-bold text-pt-text mt-1">{cases.length}</p>
            </div>
            <MapPin className="w-8 h-8 text-pt-gold" />
          </div>
        </div>
      </div>
    </div>
  );
}
